import { Button } from 'UI';
import { PaymentDtoLevelEnum, PaymentDtoTypeEnum } from 'api/generated';
import httpClient from 'api/https-client';
import { FC, useState } from 'react';
import { selectors, useAppSelector } from 'store';
import styled from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

// MVP === gold tier, held via a lifetime purchase, an installment plan, or a recurring subscription.
const MVP_PAID_TYPES: PaymentDtoTypeEnum[] = [
  PaymentDtoTypeEnum.Purchase, // lifetime MVP / gold
  PaymentDtoTypeEnum.Subscription, // monthly / annual MVP subscription
  PaymentDtoTypeEnum.Installment, // MVP installment plan
];

const MesoBonus: FC = () => {
  const level = useAppSelector(selectors.courses.selectCurrentPaymentLevel);
  const type = useAppSelector(selectors.courses.selectCurrentPaymentType);
  const [isLoading, setIsLoading] = useState(false);

  const isMvpGold = level === PaymentDtoLevelEnum.Gold && !!type && MVP_PAID_TYPES.includes(type);

  // Only MVP / gold members ever see this section (installments, subscriptions, or lifetime purchase).
  if (!isMvpGold) {
    return null;
  }

  const handleClaim = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    trackAmplitudeEvent('Meso Claim Pressed');

    try {
      // Mint a FRESH, short-lived (60 s, single-use) handoff URL on every click — never cache it.
      const { status, data } = await httpClient.get<{ url: string }>('/meso/handoff-url');

      if (status >= 200 && status < 300 && data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      // Graceful degrade — surface nothing scary to the member; logged for debugging.
      console.error('Failed to build Meso handoff URL', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section>
      <Badge>★ MVP bonus</Badge>
      <TextBlock>
        <Title>Your free Meso Core access</Title>
        <Description>
          As an MVP member, you get free access to Meso Core — claim it to start building your coaching business.
        </Description>
      </TextBlock>
      <StyledButton variant="primary" size="small" isLoading={isLoading} onClick={handleClaim}>
        Claim your Meso Core access →
      </StyledButton>
    </Section>
  );
};

export default MesoBonus;

// Section is gated to MVP / gold members only.
// Pre-launch toggle: display: none until direct card is enabled
const Section = styled.div`
  /*display: flex;*/
  display: none;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  width: 100%;
  margin-top: 16px;
  padding: 20px 24px;
  border-radius: 16px;
  background-image: linear-gradient(135deg, #cde5ff 0%, #d8ccff 55%, #e8cdff 100%);
  box-shadow: 0 12px 28px -14px rgba(80, 120, 220, 0.55);

  ${respondToWidth.s`
    padding: 16px;
  `}
`;

const Badge = styled.span`
  ${({ theme: { typography } }) => typography.footnote_semibold_12};
  color: ${({ theme: { colors } }) => colors.primary[1]};
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.div`
  ${({ theme: { typography } }) => typography.headline_semibold_18};
  color: #1e293b;
`;

const Description = styled.div`
  ${({ theme: { typography } }) => typography.body_basic_medium_14};
  color: #475569;
  line-height: 1.45;
  max-width: 460px;
`;

const StyledButton = styled(Button)`
  width: auto;
  margin-top: 4px;
  padding: 10px 24px;
  white-space: nowrap;

  ${respondToWidth.s`
    width: 100%;
  `}
`;
